import {
  Autocomplete,
  Box,
  CircularProgress,
  DialogProps,
  FormControl,
  FormHelperText,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Stack,
  SxProps,
  TextField,
  TextFieldProps,
  Theme,
  outlinedInputClasses,
} from "@mui/material";
import { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import {
  chain,
  debounce,
  isArray,
  isEmpty,
  isFunction,
  isString,
  orderBy,
  throttle,
  uniqBy,
} from "lodash-es";
import React, {
  DependencyList,
  HTMLAttributes,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useInfiniteScroll from "react-infinite-scroll-hook";

import { AxiosHelper } from "@/common/helpers/axios";
import { EntityHelper } from "@/common/helpers/entity";
import { PaginationHelper } from "@/common/helpers/pagination";
import { TypeHelper } from "@/common/helpers/type";
import { useEffectWithDeepCompare } from "@/common/hooks/effect/useEffectWithDeepCompare";
import { useMemoWithDeepCompare } from "@/common/hooks/memo/useMemoWithDeepCompare";
import {
  AutocompleteOption,
  AutocompleteOptionType,
  EntitiesToAutocompleteOptionsFunc,
  EntityToAutocompleteOptionFunc,
  createOptionForNoOptions,
  createOptionFromInputValue,
} from "@/common/ts/autocomplete";
import { EntityDefaultValuesMeta } from "@/common/ts/entity";
import { PaginationDtoOfTItemTyped } from "@/common/ts/pagination";
import { BaseEntityDto } from "@/core/api/generated/v1";

import AppIconButton, { AppIconButtonProps } from "../../Button/AppIconButton";
import AppIcon from "../../Icons/AppIcon";
import AppTooltip from "../../Tooltip/AppTooltip";
import { BaseEntityCreateUpdateModalInheritableProps } from "./BaseEntityCreateUpdateModal";
import { EntityDefaultValuesMatchStatusDisplay } from "./EntityDefaultValuesMatchStatusDisplay";

export type AutocompleteSortByFunc<TEntity extends BaseEntityDto> = (
  option: AutocompleteOption<TEntity>,
) => string;

export type AutocompleteSortOrder = "asc" | "desc";

export type AutocompleteGroupByFunc<TEntity extends BaseEntityDto> = (
  option: AutocompleteOption<TEntity>,
) => string;

export type AutocompleteRenderGroupTitleParams<TEntity extends BaseEntityDto> = {
  group: string;
  options: AutocompleteOption<TEntity>[];
  props: HTMLAttributes<HTMLDivElement> & { key?: string };
};

export function sortOptions<TEntity extends BaseEntityDto>(params: {
  options: AutocompleteOption<TEntity>[];
  sortBy: AutocompleteSortByFunc<TEntity> | null | undefined;
  sortOrder: AutocompleteSortOrder | null | undefined;
}) {
  // NB: the options must be sorted with the same dimension that they are grouped by, otherwise, you will notice duplicate headers.
  return chain(params.options)
    .groupBy((x) => x.groupBy)
    .mapValues((v, k) =>
      params.sortBy
        ? orderBy(v, (x) => params.sortBy && params.sortBy(x), params.sortOrder || "asc")
        : v,
    )
    .values()
    .flatten()
    .value();
}

export interface BaseRequestParams {
  limit?: number;
  search?: string;
  includeIds?: string[];
}

// export const baseRequestParamsNamesMap: Record<keyof BaseRequestParams, boolean> = {
//   limit: true,
//   search: true,
//   includeIds: true,
// };
// export const baseRequestParamsNames: string[] = Object.keys(baseRequestParamsNamesMap);

export type EntitySearchResponseData<TEntity> = TEntity[] | PaginationDtoOfTItemTyped<TEntity>;

export type EntitySearchRequestFuncParameters<
  TEntity,
  TRequestFunc extends (
    requestParameters: any,
    options?: AxiosRequestConfig,
  ) => Promise<AxiosResponse<EntitySearchResponseData<TEntity>>>,
> = Parameters<TRequestFunc>[0] & BaseRequestParams;

export interface BaseEntitySearchAutocompleteProps<
  TEntity extends BaseEntityDto,
  // TRequestFunc extends (...args: any) => Promise<AxiosResponse<EntitySearchResponseData<TEntity>>>,
  TRequestFunc extends (
    requestParameters: any,
    requestOptions?: AxiosRequestConfig,
  ) => Promise<AxiosResponse<EntitySearchResponseData<TEntity>>>,
  TRequestParams extends EntitySearchRequestFuncParameters<TEntity, TRequestFunc> =
    EntitySearchRequestFuncParameters<TEntity, TRequestFunc>,
> {
  // generic props
  entityId?: string | null;
  entity?: TEntity | null;
  /** On mount, initializes entity if not set by triggering `onChange`. NB: it's not the same as `defaultValue`. */
  initialEntity?: TEntity | null;
  entityToOption: EntityToAutocompleteOptionFunc<TEntity>;
  /** Load data even if the autocomplete is closed.*/
  isPreload?: boolean;
  /** Auto-select single option if the input is required and not disabled. */
  isAutoSelectSingleOption?: boolean;
  /** Auto-select first option if the input is required and not disabled. */
  isAutoSelectFirstOption?: boolean;
  /** Hack to force refetch on deps change by skipping load guards */
  isIgnoreCanLoadCondition?: boolean;
  /** Request configuration. */
  request: {
    requestFunc: TRequestFunc;
    limit?: number;
    parameters: TRequestParams;
    /** Function to correctly combine current params with the new params. */
    combineParameters: (params: TRequestParams, newParams: BaseRequestParams) => TRequestParams;
    deps: DependencyList;
    skip?: boolean;
  };
  /** Additional request configuration. */
  requestConfig?: {
    limit?: number;
    skip?: boolean;
  };

  // component props
  renderOption: (
    props: HTMLAttributes<HTMLLIElement> & { key: string },
    option: AutocompleteOption<TEntity>,
  ) => ReactNode;
  renderGroupTitle?: (params: AutocompleteRenderGroupTitleParams<TEntity>) => ReactNode;
  entityViewModal?: {
    /** @default true */
    if?: boolean;
    renderModal: (params: {
      entity: TEntity;
      dialogProps: Pick<DialogProps, "open" | "onClose">;
    }) => ReactNode;
  };
  entityEditModal?: {
    /** @default true */
    if?: boolean;
    renderModal: (params: {
      entity: TEntity;
      dialogProps: Pick<BaseEntityCreateUpdateModalInheritableProps, "open" | "onClose">;
      handleEntityUpdated: (newEntity: TEntity) => void;
    }) => ReactNode;
  };
  endAdornmentActions?: {
    /** @default true */
    if?: boolean;
    actions: Array<{
      /** @default true */
      if?: boolean;
      renderAction: (params: {
        actionIndex: number;
        entity: Nil<TEntity>;
        iconButtonProps: Partial<AppIconButtonProps>;
        handleEntitySelected: (newEntity: TEntity) => void;
      }) => ReactNode;
    }>;
  };
  disabled?: boolean;
  required?: boolean;
  size?: "small" | "medium";
  fullWidth?: boolean;
  label?: TextFieldProps["label"];
  /** If `true`, hide the selected options from the list box. */
  filterSelectedOptions?: boolean;
  placeholder: TextFieldProps["placeholder"];
  sortBy?: AutocompleteSortByFunc<TEntity>;
  sortOrder?: AutocompleteSortOrder;
  groupBy?: AutocompleteGroupByFunc<TEntity>;
  withCreate?: boolean;
  createOptionTitle?: string | ((inputValue: string) => string) | null;
  textFieldProps?: TextFieldProps;
  /** Meta for displaying extra status information. */
  defaultValuesMeta?: EntityDefaultValuesMeta | null;
  debugKey?: string;
  sx?: SxProps<Theme>;
  /** If `true`, the entity option will be disabled. */
  shouldDisable?: (entity: TEntity) => boolean;
  /** If `true`, the entity option will be hidden. */
  shouldHide?: (entity: TEntity) => boolean;
  onChange?: (newEntity?: TEntity) => void;
  /** Works only if withCreate is true. */
  onCreate?: (newOption?: AutocompleteOption<TEntity>) => void;
  /** Fired when new entities were fetched. */
  onLoaded?: (entities: TEntity[]) => void;
  onPaginatedLoaded?: (entities: PaginationDtoOfTItemTyped<TEntity>) => void;
  /** Fired when new selected entity is fetched. */
  onSelectedLoaded?: (entity: TEntity) => void;
}

/** Interface to be used by wrapper components that utilize this component. */
export interface BaseEntitySearchAutocompleteInheritableProps<TEntity extends BaseEntityDto> {
  entityId?: string | null;
  entity?: TEntity | null;
  /** On mount, initializes entity if not set by triggering `onChange`. NB: it's not the same as `defaultValue`. */
  initialEntity?: TEntity | null;
  /** Load data even if the autocomplete is closed.*/
  isPreload?: boolean;
  /** Auto-select single option if the input is required and not disabled. */
  isAutoSelectSingleOption?: boolean;
  /** Auto-select first option if the input is required and not disabled. */
  isAutoSelectFirstOption?: boolean;
  /** Additional request configuration. */
  requestConfig?: {
    limit?: number;
    skip?: boolean;
  };
  disabled?: boolean;
  required?: boolean;
  size?: "small" | "medium";
  fullWidth?: boolean;
  label?: TextFieldProps["label"];
  /** If `true`, hide the selected options from the list box. */
  filterSelectedOptions?: boolean;
  withCreate?: boolean;
  createOptionTitle?: string | ((inputValue: string | null | undefined) => string) | null;
  textFieldProps?: TextFieldProps;
  /** Meta for displaying extra status information. */
  defaultValuesMeta?: EntityDefaultValuesMeta | null;
  debugKey?: string;
  sx?: SxProps<Theme>;
  /** If `true`, the entity option will be disabled. */
  shouldDisable?: (entity: TEntity) => boolean;
  /** If `true`, the entity option will be hidden. */
  shouldHide?: (entity: TEntity) => boolean;
  onChange?: (newEntity?: TEntity) => void;
  /** Works only if withCreate is true. */
  onCreate?: (newOption?: AutocompleteOption<TEntity>) => void;
  /** Fired when new entities are fetched. */
  onLoaded?: (entities: TEntity[]) => void;
  onPaginatedLoaded?: (entities: PaginationDtoOfTItemTyped<TEntity>) => void;
  /** Fired when new selected entity is fetched. */
  onSelectedLoaded?: (entity: TEntity) => void;
}

export default function BaseEntitySearchAutocomplete<
  TEntity extends BaseEntityDto,
  TRequestFunc extends (
    requestParameters: any,
    options?: AxiosRequestConfig,
  ) => Promise<AxiosResponse<EntitySearchResponseData<TEntity>>>,
  TRequestParams extends EntitySearchRequestFuncParameters<TEntity, TRequestFunc> =
    EntitySearchRequestFuncParameters<TEntity, TRequestFunc>,
>({
  entityId,
  entity,
  initialEntity,
  entityToOption,
  isPreload = true,
  isAutoSelectSingleOption = true,
  isAutoSelectFirstOption = false,
  isIgnoreCanLoadCondition = false,
  request,
  requestConfig,
  renderOption,
  renderGroupTitle,
  entityViewModal,
  entityEditModal,
  endAdornmentActions,
  disabled,
  required,
  size,
  fullWidth,
  filterSelectedOptions = false,
  label,
  placeholder,
  sortBy,
  sortOrder,
  groupBy,
  withCreate,
  createOptionTitle,
  textFieldProps,
  defaultValuesMeta,
  debugKey,
  shouldDisable,
  shouldHide,
  onChange,
  onCreate,
  onLoaded,
  onPaginatedLoaded,
  onSelectedLoaded,
  sx,
  ...otherProps
}: BaseEntitySearchAutocompleteProps<TEntity, TRequestFunc>) {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<readonly AutocompleteOption<TEntity>[]>([]);
  const [selectedOption, setSelectedOption] = useState<AutocompleteOption<TEntity> | null>(null);

  const [inputValue, setInputValue] = useState<string | null | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [searchResponseData, setSearchResponseData] = useState<
    EntitySearchResponseData<TEntity> | undefined
  >(undefined);
  const [isEntityViewModalOpen, setIsEntityViewModalOpen] = useState(false);
  const [isEntityEditModalOpen, setIsEntityEditModalOpen] = useState(false);

  const isRequestStartedRef = useRef(false);
  const isAutoSelectedRef = useRef(false);

  // just track 401 and 403 to show error to user but do not disable the input to allow user to refresh it manually
  // or in case the next request will succeed
  const axiosErrorRef = useRef<AxiosError>(null);
  const isUnauthenticated = axiosErrorRef.current?.response?.status === 401;
  const isForbidden = axiosErrorRef.current?.response?.status === 403;

  const entityIdComputed = entityId || entity?.id || selectedOption?.data?.id;
  const isSelectedOptionLoaded = !entityId || (entityId && options.some((x) => x.id === entityId));
  const canLoad =
    ((isPreload || (!isPreload && isOpen)) && !selectedOption) ||
    (selectedOption?.title === inputValue && !isSelectedOptionLoaded);
  const canSearch =
    (!selectedOption && !isSelectedOptionLoaded) ||
    (selectedOption?.title !== inputValue && inputValue !== undefined);
  const disabledComputed = disabled;

  const optionsGroupedMap = useMemo(
    () =>
      options &&
      chain(options)
        .groupBy((x) => x.groupBy)
        .value(),
    [options],
  );

  const entityList = useMemo(
    () => (searchResponseData && isArray(searchResponseData) ? searchResponseData : undefined),
    [searchResponseData],
  );
  const paginatedEntities = useMemo(
    () => (searchResponseData && !isArray(searchResponseData) ? searchResponseData : undefined),
    [searchResponseData],
  );
  const entityListComputed = useMemo(
    () => entityList || paginatedEntities?.items || undefined,
    [entityList, paginatedEntities],
  );
  const selectedEntityComputed = useMemo(
    () => entityListComputed?.find((x) => x.id === entityIdComputed) || undefined,
    [entityIdComputed, entityListComputed],
  );

  const defaultValuesMatchStatus = useMemo(
    () =>
      EntityHelper.computeDefaultValuesMatchStatusForAutocomplete({
        defaultValuesMeta: defaultValuesMeta,
        selectedEntityId: entityIdComputed,
      }),
    [defaultValuesMeta, entityIdComputed],
  );

  const entitiesToOptions = useMemo<EntitiesToAutocompleteOptionsFunc<TEntity>>(
    () => (entities) => (entities && entities.map((x) => entityToOption(x))) || [],
    [entityToOption],
  );

  const requestComputed = useMemo<typeof request>(
    () => ({
      ...request,
      ...requestConfig,
      limit: requestConfig?.limit ?? request.limit ?? PaginationHelper.defaultLimit,
      skip: request.skip || requestConfig?.skip,
    }),
    [request, requestConfig],
  );

  const currentLimitRef = useRef<number | undefined>(requestComputed.limit);

  const computeParameters = useCallback(
    (limit?: number, searchValue?: string | null, id?: string | null) => {
      const baseSearchParams: BaseRequestParams = {
        limit: limit,
        search: canSearch || !options.length ? searchValue || undefined : undefined,
        includeIds:
          id && !isSelectedOptionLoaded && selectedOption?.title === searchValue ? [id] : undefined,
      };
      return requestComputed.combineParameters(requestComputed.parameters, baseSearchParams);
    },
    [requestComputed.parameters, options, canSearch, isSelectedOptionLoaded, selectedOption],
  );

  // compute request params
  const parametersComputed = useMemoWithDeepCompare(() => {
    return computeParameters(currentLimitRef.current, inputValue, entityId);
  }, [entityId, inputValue, requestComputed.parameters, currentLimitRef.current]);

  const searchRef = useRef(requestComputed.requestFunc);
  const searchComputed = useCallback(async (requestParams: TRequestParams) => {
    if (isRequestStartedRef.current) {
      return;
    }

    isRequestStartedRef.current = true;
    setIsLoading(true);

    try {
      const response = await searchRef.current(requestParams);
      axiosErrorRef.current = null;
      setSearchResponseData(response.data);
    } catch (e) {
      axiosErrorRef.current = AxiosHelper.getAxiosErrorOrNull(e);
      setSearchResponseData(undefined);
    } finally {
      isRequestStartedRef.current = false;
      setIsLoading(false);
    }
  }, []);
  const searchThrottle = useCallback(
    throttle(searchComputed, 500, { leading: true, trailing: false }),
    [searchComputed],
  );
  const searchDebounce = useCallback(
    debounce(searchComputed, 500, { leading: false, trailing: true }),
    [searchComputed],
  );

  // refetch on deps change
  useEffectWithDeepCompare(() => {
    if (!isIgnoreCanLoadCondition && !canLoad) {
      return;
    }
    if (requestComputed.skip) {
      return;
    }
    if (isRequestStartedRef.current) {
      return;
    }

    currentLimitRef.current = requestComputed.limit;
    const params = computeParameters(currentLimitRef.current, inputValue, entityId);
    searchThrottle(params);
  }, [requestComputed.skip, entityId, isIgnoreCanLoadCondition, canLoad, ...requestComputed.deps]);

  // refetch on search input
  useEffect(() => {
    if (!canLoad && !canSearch) {
      return;
    }
    if (requestComputed.skip) {
      return;
    }
    if (isRequestStartedRef.current) {
      return;
    }

    currentLimitRef.current = requestComputed.limit;
    const params = computeParameters(currentLimitRef.current, inputValue, entityId);
    searchDebounce(params);
  }, [inputValue, canLoad, canSearch]);

  // entities loaded
  useEffect(() => {
    if (entityListComputed) {
      onLoaded?.(entityListComputed);
    }
  }, [entityListComputed]);

  // paginated entities loaded
  useEffect(() => {
    if (paginatedEntities) {
      onPaginatedLoaded?.(paginatedEntities);
    }
  }, [paginatedEntities]);
  // paginated entities loaded
  useEffect(() => {
    if (selectedEntityComputed) {
      onSelectedLoaded?.(selectedEntityComputed);
    }
  }, [selectedEntityComputed]);

  // construct new options
  useEffect(() => {
    let newOptions = uniqBy(
      [
        ...(selectedOption && selectedOption.title === inputValue ? [selectedOption] : []),
        ...entitiesToOptions(entityListComputed || []),
      ],
      (x) => x.id,
    );
    newOptions = sortOptions({ options: newOptions, sortBy, sortOrder });
    setOptions(newOptions);

    // handle isAutoSelectSingleOption, isAutoSelectFirstOption
    const singleOption = newOptions.length === 1 ? newOptions[0] : undefined;
    const firstOption = newOptions.length >= 1 ? newOptions[0] : undefined;
    const canAutoSelectOption =
      required &&
      !disabledComputed &&
      !isRequestStartedRef.current &&
      !isLoading &&
      !entityId &&
      !entity &&
      !selectedOption &&
      !inputValue &&
      !isAutoSelectedRef.current;
    if (isAutoSelectSingleOption && canAutoSelectOption && singleOption) {
      setSelectedOption(singleOption);
      onChange && onChange(singleOption.data || undefined);
      isAutoSelectedRef.current = true;
    } else if (isAutoSelectFirstOption && canAutoSelectOption && firstOption) {
      setSelectedOption(firstOption);
      onChange && onChange(firstOption.data || undefined);
      isAutoSelectedRef.current = true;
    }
  }, [entityListComputed]);

  // update selected option when entity/entityId change from outside
  useEffect(() => {
    if (!entity && !entityId && selectedOption) {
      setSelectedOption(null);
      setInputValue(undefined);
    } else if (entity && selectedOption?.id !== entity.id) {
      setOptions(
        sortOptions({
          options: uniqBy([entityToOption(entity), ...options], (x) => x.id),
          sortBy,
          sortOrder,
        }),
      );
      setSelectedOption(entityToOption(entity));
    } else if (entityId && selectedOption?.id !== entityId) {
      setSelectedOption(options.find((x) => x.id === entityId) || null);
    }
  }, [entityId, entity, selectedOption, options]);

  // apply initialEntity on mount
  useEffect(() => {
    if (!entity && !entityId && initialEntity) {
      onChange && onChange(initialEntity);
    }
  }, []);

  const [sentryRef, { rootRef }] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage:
      (paginatedEntities?.pagination?.limit || 0) <
      (paginatedEntities?.pagination?.totalCount || 0),
    onLoadMore: () => {
      currentLimitRef.current = (currentLimitRef.current || 0) + (requestComputed.limit || 0);
      searchComputed(parametersComputed);
    },
    rootMargin: "0px 0px 300px 0px",
  });

  const sentryOption: AutocompleteOption<TEntity> = useMemo(
    () => ({ id: "sentry", title: "", optionType: AutocompleteOptionType.Sentry }),
    [],
  );

  return (
    <Autocomplete
      sx={{ minWidth: 200, flex: 1, ...sx }}
      fullWidth={fullWidth}
      size={size}
      disabled={disabledComputed}
      filterSelectedOptions={filterSelectedOptions}
      open={isOpen}
      options={options || []}
      loading={isLoading}
      autoComplete
      openOnFocus
      blurOnSelect
      includeInputInList
      value={selectedOption}
      inputValue={inputValue || ""}
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      freeSolo={withCreate}
      ListboxProps={{ ref: rootRef }}
      groupBy={groupBy}
      onOpen={() => {
        setIsOpen(true);
      }}
      onClose={() => {
        setIsOpen(false);
      }}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={(option) => {
        // value selected with enter, right from the input
        if (typeof option === "string") {
          return option;
        }
        // add "xxx" option created dynamically
        if (option.inputValue) {
          return option.inputValue;
        }
        // regular option
        return option.title;
      }}
      filterOptions={(options2, params) => {
        // suggest the creation of a new value (add dynamic option with input value)
        if (withCreate) {
          const isExisting = options.some((option) => params.inputValue === option.title);
          if (params.inputValue !== "" && !isExisting) {
            const title =
              (createOptionTitle &&
                ((isFunction(createOptionTitle) && createOptionTitle(params.inputValue)) ||
                  (isString(createOptionTitle) && createOptionTitle))) ||
              `Create new "${params.inputValue}"`;

            options2.unshift(
              createOptionFromInputValue({
                inputValue: params.inputValue,
                title: title,
              }),
            );
          }
        }

        if (
          !isEmpty(options2) &&
          (paginatedEntities?.pagination?.limit || 0) <
            (paginatedEntities?.pagination?.totalCount || 0)
        ) {
          // add sentry option for infinite scroll
          options2.push(sentryOption);
        }

        // add 'No data' option
        if (isEmpty(options2) && !isLoading) {
          options2.push(createOptionForNoOptions());
        }

        return options2;
      }}
      onChange={(e, newValue) => {
        // value selected with enter, right from the input
        if (typeof newValue === "string") {
          withCreate &&
            onCreate &&
            onCreate(
              createOptionFromInputValue({
                inputValue: newValue,
              }),
            );
          return;
        } else if (newValue && newValue.inputValue) {
          // create a new value from the user input
          withCreate && onCreate && onCreate(newValue);
          return;
        } else {
          setSelectedOption(newValue || null);
          onChange && onChange(newValue?.data || undefined);
        }
      }}
      onInputChange={(e, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <FormControl fullWidth error={textFieldProps?.error}>
          <TextField
            {...textFieldProps}
            {...params}
            sx={{
              ...textFieldProps?.sx,

              // show action on hover
              "& .BaseEntitySearchAutocomplete-endAdornment-action": {
                visibility: "hidden",
              },
              "&:hover .BaseEntitySearchAutocomplete-endAdornment-action": {
                visibility: "visible",
              },
              [`& .${outlinedInputClasses.focused} .BaseEntitySearchAutocomplete-endAdornment-action`]:
                {
                  visibility: "visible",
                },
            }}
            error={textFieldProps?.error}
            required={required}
            label={label || textFieldProps?.label}
            placeholder={placeholder || textFieldProps?.placeholder}
            fullWidth
            InputProps={{
              ...textFieldProps?.InputProps,
              ...params.InputProps,
              startAdornment: textFieldProps?.InputProps?.startAdornment,
              endAdornment: (
                <InputAdornment position='end'>
                  {textFieldProps?.InputProps?.endAdornment || (
                    <>
                      {/* Quick view */}
                      {selectedOption?.data &&
                        TypeHelper.isNilOrTrue(entityViewModal?.if) &&
                        entityViewModal?.renderModal && (
                          <>
                            <AppIconButton
                              className='BaseEntitySearchAutocomplete-endAdornment-action'
                              disabled={disabledComputed}
                              size='small'
                              shape='round'
                              title='Quick view'
                              onClick={(e) => {
                                e.stopPropagation(); // prevent autocomplete thinking it's open
                                setIsEntityViewModalOpen(true);
                              }}
                            >
                              <AppIcon of='quickView' />
                            </AppIconButton>

                            {entityViewModal.renderModal({
                              entity: selectedOption.data!,
                              dialogProps: {
                                open: isEntityViewModalOpen,
                                onClose: () => {
                                  setIsEntityViewModalOpen(false);
                                },
                              },
                            })}
                          </>
                        )}

                      {/* Quick edit */}
                      {selectedOption?.data &&
                        TypeHelper.isNilOrTrue(entityEditModal?.if) &&
                        entityEditModal?.renderModal && (
                          <>
                            <AppIconButton
                              className='BaseEntitySearchAutocomplete-endAdornment-action'
                              disabled={disabledComputed}
                              size='small'
                              shape='round'
                              title='Quick edit'
                              onClick={(e) => {
                                e.stopPropagation(); // prevent autocomplete thinking it's open
                                setIsEntityEditModalOpen(true);
                              }}
                            >
                              <AppIcon of='edit' />
                            </AppIconButton>

                            {entityEditModal.renderModal({
                              entity: selectedOption.data!,
                              dialogProps: {
                                open: isEntityEditModalOpen,
                                onClose: () => {
                                  setIsEntityEditModalOpen(false);
                                },
                              },
                              handleEntityUpdated: (newEntity) => {
                                const newOption = entityToOption(newEntity);
                                setSelectedOption(newOption);
                                onChange && onChange(newOption?.data || undefined);
                              },
                            })}
                          </>
                        )}

                      {/* Actions */}
                      {TypeHelper.isNilOrTrue(endAdornmentActions?.if) &&
                        endAdornmentActions?.actions
                          .filter((action) => TypeHelper.isNilOrTrue(action.if))
                          .map((action, actionIndex) => (
                            <React.Fragment key={actionIndex}>
                              {action.renderAction({
                                actionIndex,
                                entity: selectedOption?.data,
                                iconButtonProps: {
                                  className: "BaseEntitySearchAutocomplete-endAdornment-action",
                                  disabled: disabledComputed,
                                  size: "small",
                                  shape: "round",
                                  onClick: (e) => {
                                    e.stopPropagation(); // prevent autocomplete thinking it's open
                                  },
                                },
                                handleEntitySelected: (newEntity) => {
                                  const newOption = entityToOption(newEntity);
                                  setSelectedOption(newOption);
                                  onChange && onChange(newOption?.data || undefined);
                                },
                              })}
                            </React.Fragment>
                          ))}

                      {/* Refresh */}
                      {isLoading ? (
                        <CircularProgress color='inherit' size={20} />
                      ) : (
                        <AppIconButton
                          // using visibility like Mui clear button
                          className='BaseEntitySearchAutocomplete-endAdornment-action'
                          disabled={disabledComputed}
                          size='small'
                          shape='round'
                          title='Refresh'
                          onClick={(e) => {
                            searchComputed(parametersComputed);
                          }}
                        >
                          <AppIcon of='refresh' />
                        </AppIconButton>
                      )}

                      {params.InputProps.endAdornment}
                    </>
                  )}
                </InputAdornment>
              ),
            }}
          />

          {isUnauthenticated && (
            <FormHelperText error>
              {`You aren't authenticated`}{" "}
              <AppTooltip title='This might be a transient error, or your session has expired. Please try to refresh the input, refresh the page or re-login.'>
                <AppIcon of='help' inText />
              </AppTooltip>
            </FormHelperText>
          )}
          {isForbidden && (
            <FormHelperText error>
              {`You don't have enough permissions`}{" "}
              <AppTooltip
                title={
                  <Box>
                    <Box>
                      {`You don't have the permissions required to access this resource. Check with
                      your company admin for extended permissions.`}
                    </Box>
                    <Box>
                      This might also be a transient error. You can try to refresh the input,
                      refresh the page, or re-login.
                    </Box>
                  </Box>
                }
              >
                <AppIcon of='help' inText />
              </AppTooltip>
            </FormHelperText>
          )}

          {defaultValuesMatchStatus && (
            <EntityDefaultValuesMatchStatusDisplay
              status={defaultValuesMatchStatus}
              isAllowCreate={false} // just show status without any controls
              onCreate={null}
              onClick={(e) => {
                e.stopPropagation(); // prevent autocomplete thinking it's open
              }}
            />
          )}
        </FormControl>
      )}
      renderGroup={
        groupBy
          ? (params) => {
              const optionsInGroup = optionsGroupedMap[params.group] || [];
              const titleRendered = renderGroupTitle
                ? renderGroupTitle({
                    group: params.group,
                    options: optionsInGroup,
                    props: {},
                  })
                : undefined;

              return (
                <ListItem key={params.key} disablePadding>
                  <Stack sx={{ width: "100%" }}>
                    <ListSubheader component='div' sx={{ position: "sticky", top: "-8px" }}>
                      {titleRendered || params.group}
                    </ListSubheader>
                    <List disablePadding>{params.children}</List>
                  </Stack>
                </ListItem>
              );
            }
          : undefined
      }
      renderOption={(props, option) => {
        if (option.optionType === AutocompleteOptionType.Sentry) {
          return (
            <ListItem ref={sentryRef}>
              <ListItemText>Loading...</ListItemText>
            </ListItem>
          );
        }

        const isHidden = option.data && shouldHide ? shouldHide(option.data) : false;
        if (isHidden) {
          return null;
        }
        const isDisabled = option.data && shouldDisable ? shouldDisable(option.data) : false;
        return renderOption(
          {
            ...props,
            key: option.id,
            style: {
              ...props?.style,
              cursor: "default",
              ...(option.optionType === AutocompleteOptionType.NoOptions
                ? {
                    cursor: "default",
                  }
                : undefined),
              ...(isDisabled
                ? {
                    pointerEvents: "none",
                    opacity: 0.5,
                  }
                : undefined),
            },
          },
          option,
        );
      }}
      {...otherProps}
    />
  );
}
